import { useCallback, useEffect, useRef } from "react";
import {
  CompanionStreamingService,
  StreamState,
} from "@service/companion-streaming.service";
import { TextBuffer } from "@service/text-buffer";
import { AudioMessageStream } from "@models/audio-message-stream";
import { EventType } from "@models/audio-message";
import { removeMarkdown } from "@utils/text.util";
import { debugCompanionPacing } from "@utils/companion-pacing-debug";
import { useDispatch } from "react-redux";
import { addText, setStreamState, setText } from "@store/avatarSlice";

/** While sliding gallery, reject tagged assistant chunks until the next `NewPhoto`/`NewConversation` send assigns a new seq. */
const STREAM_SEQ_AWAITING_NEW_ASSISTANT_TURN = -1;
/** User mic streaming uses the same TextBuffer path; do not apply assistant turn matching. */
const STREAM_SEQ_USER_UTTERANCE = -2;

export const useCompanionStreamingService = (
  speak: (text: string) => void,
  evictFullSentence = true,
  ttsEvictionMs = 500,
): {
  send: (message: AudioMessageStream) => Promise<void>;
  suppressGalleryAssistantStream: () => void;
} => {
  const streamingService = useRef<CompanionStreamingService | null>(null);
  const textBuffer = useRef<TextBuffer | null>(null);
  const streamSeqRef = useRef(0);
  const latestStreamSeqRef = useRef(0);
  /** Mic sends many chunks per utterance; only the first should reset buffer / seq bookkeeping. */
  const userAudioBurstActiveRef = useRef(false);
  /** Seq for the current assistant `TextBuffer` turn; compared to `latestStreamSeqRef` so slides invalidate pending TTS. */
  const activeAssistantUtteranceSeqRef = useRef(STREAM_SEQ_USER_UTTERANCE);
  const dispatch = useDispatch();

  const cleanSpeak = useCallback(
    (text: string): void => {
      const turn = activeAssistantUtteranceSeqRef.current;
      if (
        turn >= 0 &&
        latestStreamSeqRef.current !== turn
      ) {
        return;
      }
      const cleanText = removeMarkdown(text, {});
      speak(cleanText);
      dispatch(addText(cleanText));
    },
    [dispatch, speak],
  );

  const handleStateChange = useCallback(
    (streamState: StreamState) => {
      dispatch(setStreamState(streamState));
      if (streamState === StreamState.Completed) {
        userAudioBurstActiveRef.current = false;
      }
    },
    [dispatch],
  );

  const handleResponse = useCallback((text: string, streamSeq?: number) => {
    if (!textBuffer.current) {
      return;
    }
    const latest = latestStreamSeqRef.current;
    if (streamSeq != null) {
      if (
        latest === STREAM_SEQ_AWAITING_NEW_ASSISTANT_TURN ||
        streamSeq !== latest
      ) {
        return;
      }
    } else {
      // Streaming UserInput replies omit streamSeq; still drop stale chunks after a gallery suppress.
      if (
        latest === STREAM_SEQ_AWAITING_NEW_ASSISTANT_TURN &&
        !userAudioBurstActiveRef.current
      ) {
        return;
      }
    }
    textBuffer.current.addText(text);
  }, []);

  useEffect(() => {
    const service = new CompanionStreamingService(
      handleResponse,
      handleStateChange,
    );
    streamingService.current = service;

    return () => {
      textBuffer.current?.close();
      textBuffer.current = null;
      service?.destroy();
    };
  }, [handleResponse, handleStateChange]);

  const send = useCallback(
    async (message: AudioMessageStream): Promise<void> => {
      if (!streamingService.current) {
        return;
      }

      if (
        message.endUserUtterance === true &&
        message.eventType === EventType.UserInput
      ) {
        await streamingService.current.sendMessage({
          ...message,
          audio: null,
          streamSeq: undefined,
          endUserUtterance: true,
        });
        userAudioBurstActiveRef.current = false;
        return;
      }

      if (
        message.abortUserUtterance === true &&
        message.eventType === EventType.UserInput
      ) {
        // Gallery slide while PTT was recording: server drops the in-flight
        // utterance. Park the seq at AWAITING so any late PCM chunks (until
        // the mic actually stops) won't spawn a new user-turn TextBuffer that
        // would steal subtitles from the upcoming NewPhoto reply.
        await streamingService.current.sendMessage({
          ...message,
          audio: null,
          streamSeq: undefined,
          abortUserUtterance: true,
        });
        userAudioBurstActiveRef.current = false;
        latestStreamSeqRef.current = STREAM_SEQ_AWAITING_NEW_ASSISTANT_TURN;
        activeAssistantUtteranceSeqRef.current = STREAM_SEQ_USER_UTTERANCE;
        return;
      }

      const isUserAudioChunk =
        message.eventType === EventType.UserInput && message.audio != null;

      if (isUserAudioChunk) {
        if (!userAudioBurstActiveRef.current) {
          userAudioBurstActiveRef.current = true;
          activeAssistantUtteranceSeqRef.current = STREAM_SEQ_USER_UTTERANCE;
          // PTT/VAD: after a slide or PTT interrupt, `latestStreamSeqRef` is parked at
          // AWAITING. The user's streaming reply is untagged, so without exiting AWAITING
          // here `handleResponse` drops it once `endUserUtterance` clears the burst flag
          // (symptom: pushing End Talk in gallery yields no answer).
          latestStreamSeqRef.current = STREAM_SEQ_USER_UTTERANCE;
          dispatch(setText(null));
          dispatch(setStreamState(null));
          textBuffer.current?.close();
          textBuffer.current = new TextBuffer(
            cleanSpeak,
            evictFullSentence,
            ttsEvictionMs,
          );
          debugCompanionPacing("streaming: new assistant TextBuffer (user turn)", {
            ttsEvictionMs,
            conversationPacing: message.conversationPacing ?? "balanced",
          });
        }
        await streamingService.current.sendMessage({
          ...message,
          streamSeq: undefined,
        });
        return;
      }

      // Gallery / NewPhoto while PTT or VAD is still streaming: do not clear burst or the next
      // PCM chunk is treated as a "new" utterance and breaks seq + subtitle state.
      const hadActiveUserBurst = userAudioBurstActiveRef.current;
      if (!hadActiveUserBurst) {
        userAudioBurstActiveRef.current = false;
      }
      streamSeqRef.current += 1;
      const streamSeq = streamSeqRef.current;
      latestStreamSeqRef.current = streamSeq;
      activeAssistantUtteranceSeqRef.current = streamSeq;
      dispatch(setText(null));
      dispatch(setStreamState(null));
      textBuffer.current?.close();
      textBuffer.current = new TextBuffer(
        cleanSpeak,
        evictFullSentence,
        ttsEvictionMs,
      );
      debugCompanionPacing("streaming: new assistant TextBuffer (init / photo)", {
        ttsEvictionMs,
        streamSeq,
        conversationPacing: message.conversationPacing ?? "balanced",
      });
      await streamingService.current.sendMessage({ ...message, streamSeq });
    },
    [cleanSpeak, dispatch, evictFullSentence, ttsEvictionMs],
  );

  const suppressGalleryAssistantStream = useCallback(() => {
    latestStreamSeqRef.current = STREAM_SEQ_AWAITING_NEW_ASSISTANT_TURN;
    textBuffer.current?.close();
    textBuffer.current = new TextBuffer(
      cleanSpeak,
      evictFullSentence,
      ttsEvictionMs,
    );
    dispatch(setText(null));
    dispatch(setStreamState(null));
  }, [cleanSpeak, dispatch, evictFullSentence, ttsEvictionMs]);

  useEffect(() => {
    return () => {
      dispatch(setStreamState(null));
      dispatch(setText(null));
    };
  }, [dispatch]);

  useEffect(() => {
    textBuffer.current?.setEvictionTimeout(ttsEvictionMs);
  }, [ttsEvictionMs]);

  return { send, suppressGalleryAssistantStream };
};
